import * as React from "react";

import type { FilterDefinition } from "@/shared/components/ResourceView";
import { type SeriesColor } from "@/shared/domain/dashboards/DashboardDefinition";
import { TimeRange } from "@/shared/domain/dashboards/TimeRange";
import {
  ChartDisplayType,
  groupTruncationOf,
  type GroupTruncation,
  MetricDefinitionRecord,
  Period,
} from "@/shared/domain/metrics";
import {
  useAggregateMetrics,
  useInfiniteQueryMetrics,
} from "@/shared/domain/metrics/hooks";
import { NS_PER_MS } from "@/shared/time/time";
import type { Condition } from "@/types";

import { getCardTitle } from "../cardTitle";
import {
  AggregatedChartCardConfig,
  ChartCardConfig,
  effectiveChartMetric,
} from "../DashboardHeader/DashboardParams";

import { aggregateRequestFor } from "./aggregateRequest";
import type { AxisDomain } from "./axisDomain";
import { BucketWindow, instantWindow, selectionWindow } from "./bucketWindow";
import { chartSubtext, rawChartSubtext } from "./chartSubtext";
import { DrillDownDialog } from "./DrillDownDialog";
import { groupCapCaption } from "./groupCap";
import { bucketSpan, groupKeysOf } from "./groupedSeries";
import { LoadAllButton } from "./LoadAllButton";
import { MetricChart, RawMetricChart } from "./MetricChart";
import styles from "./MetricChartCard.module.css";
import { toRawSeries } from "./rawSeries";
import { rawRequestFor } from "./rawSeriesRequest";
import {
  selectionSeriesCounts,
  type SeriesCounts,
  seriesKeysOf,
} from "./seriesSuggestions";

export interface MetricChartCardProps {
  definitions: MetricDefinitionRecord[];
  /** Configuration for this card (controlled by the parent). */
  config: ChartCardConfig;
  /** This card's position among the dashboard's unresolved cards — spreads
   *  seeded (metric-less) cards across distinct definitions (see
   *  {@link effectiveChartMetric}). */
  seedIndex?: number;
  orgId: string | undefined;
  period: Period;
  range: TimeRange;
  /** The span every card on this dashboard draws, so cards agree about what is
   *  on screen rather than each ending wherever its own metric last reported. */
  axisDomain?: AxisDomain;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** The dashboard's full filter vocabulary. Only the drill-down dialog reads
   *  it — it mounts the app's filter bar over the series a click covered. */
  filterDefinitions?: FilterDefinition[];
}

/** The bucket the user drilled into, pinned with the metric/period it was
 *  opened at so the dialog stays stable if the card re-resolves meanwhile. */
interface DrilldownSelection {
  metricName: string;
  bucket: BucketWindow;
  period: Period;
  /** The group values of the series under the clicked bucket; `undefined` on
   *  an ungrouped card. */
  groupKeys?: (string | null)[];
}

/** Everything both card variants need, with the metric already resolved. */
interface ChartCardBodyProps {
  metricName: string;
  /** The card's own title, shown in place of the generated one. */
  title?: string;
  orgId: string | undefined;
  period: Period;
  range: TimeRange;
  /** The span every card on this dashboard draws, so cards agree about what is
   *  on screen rather than each ending wherever its own metric last reported. */
  axisDomain?: AxisDomain;
  deviceIds?: string[];
  sessionIds?: string[];
  /** Optional: session-property / custom-field predicate applied server-side. */
  condition?: Condition;
  /** Optional: the card's group-by path. Both bodies split their series by it;
   *  they differ only in WHERE the split happens — the aggregated one sends it
   *  to the server, the raw one applies it to the rows that come back. */
  groupBy?: string;
  /** Optional: how the card draws its series. Absent means the line both
   *  variants have always defaulted to. */
  display?: ChartDisplayType;
  /** Optional: the dashboard's filter vocabulary, for the drill-down's filter
   *  bar. Absent, the dialog offers no filters of its own. */
  filterDefinitions?: FilterDefinition[];
}

/**
 * Names the field a card's series are split by, on the card's caption line.
 *
 * It sits beside prose now rather than leading a row of swatches, so it is
 * phrased as a fact about the plot ("grouped by …") instead of a bare path —
 * on its own, `session.custom.build_id` next to "12 sessions over 3 days"
 * reads as a second, unrelated caption. The legend still carries the field in
 * its accessible name, which is where a screen reader meets the group values.
 *
 * Unlike the caption beside it, this does NOT wait on the query: the grouping
 * is the card's configuration, known before the first row lands, so it is
 * stated as soon as the card is on screen.
 *
 * When the server's group cap left groups out, it adds how many are drawn of
 * how many there are ("top 100 of 3,412"); the legend's own note says the rest.
 */
function GroupByCaption({
  groupBy,
  groupTruncation,
}: {
  groupBy: string;
  groupTruncation?: GroupTruncation | null;
}) {
  const text = groupTruncation
    ? `grouped by ${groupBy} · ${groupCapCaption(groupTruncation.totalGroups)}`
    : `grouped by ${groupBy}`;
  return (
    <span className={styles.groupBy} title={text}>
      {text}
    </span>
  );
}

/**
 * Full-width chart card: a compact title above the chart, plus whatever the
 * card's variant offers alongside it. Pure display — the
 * metric/series/aggregation/display selection is edited through the card's
 * ⋮ → "Edit Data" item, which opens the widget editor on its own route
 * that owns the {@link ChartCardConfig}.
 *
 * The two variants differ in more than a query, which is why each has its own
 * body: an aggregated card plots buckets and drills INTO one; a raw card
 * plots sessions, loads progressively, and navigates TO one. Splitting them
 * here is also what keeps each body's data hook unconditional.
 */
export function MetricChartCard({
  definitions,
  config,
  seedIndex = 0,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
  filterDefinitions,
}: MetricChartCardProps) {
  const names = definitions.map((d) => d.name);

  if (names.length === 0) {
    return null;
  }

  // Every field is spelled out rather than spread, so a prop added to
  // ChartCardBodyProps must be added here too. An OPTIONAL one that is not
  // simply vanishes: the type stays satisfied and the body silently falls back
  // to its undefined behaviour, which is how the shared axis domain reached
  // the card and got no further on its first pass.
  const body: ChartCardBodyProps = {
    metricName: effectiveChartMetric(config, names, seedIndex),
    title: config.title,
    orgId,
    period,
    range,
    axisDomain,
    deviceIds,
    sessionIds,
    condition,
    groupBy: config.group_by,
    display: config.display,
    filterDefinitions,
  };

  // Absence of an aggregation IS the raw discriminant — see
  // `RawChartCardConfig`. TypeScript narrows both ways off this test.
  return config.aggregation === undefined ? (
    <RawChartCardBody {...body} color={config.color} />
  ) : (
    <AggregatedChartCardBody {...body} config={config} />
  );
}

/**
 * An aggregated card: one point per period bucket, and a click opens the
 * drill-down dialog listing the records inside that bucket.
 *
 * The drill-down dialog is local component state (per card, so only the
 * drilled card opens); it closes on unmount, e.g. when navigating away.
 */
function AggregatedChartCardBody({
  config,
  metricName,
  title,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
  filterDefinitions,
}: ChartCardBodyProps & { config: AggregatedChartCardConfig }) {
  // The same query the chart below runs, so this reads from that cache rather
  // than fetching again (see aggregateRequestFor). Undefined while it loads —
  // the line appears with the plot rather than reserving a blank row.
  const { data } = useAggregateMetrics(
    aggregateRequestFor({
      metricName,
      period,
      aggregation: config.aggregation,
      range,
      deviceIds,
      sessionIds,
      condition,
      // Same request as the chart below, group-by included — an identical
      // request is what keeps the caption and the plot on ONE cache entry and
      // one fetch (see aggregateRequestFor).
      groupBy: config.group_by,
    }),
    orgId,
  );
  const subtext = React.useMemo(() => {
    if (!data) {
      return null;
    }
    // Every metric row belongs to exactly one group, so summing the per-bucket
    // totals still counts each session once however the response was split.
    const sessions = data.records.reduce((sum, r) => sum + r.total, 0);
    // The SPAN from first data point to last, in whole periods — the same
    // gap-filled run the chart plots on its x-axis, so the caption and the
    // plot agree about how much time is on screen. Counting records instead
    // would under-report whenever a period had no sessions at all, and
    // over-report by a factor of the group count on a grouped card.
    return chartSubtext(sessions, bucketSpan(data.records, axisDomain), period);
  }, [data, period, axisDomain]);

  // Every series the chart draws, for the drill-down to suggest first.
  const seriesKeys = React.useMemo(
    () =>
      config.group_by !== undefined && data
        ? seriesKeysOf(data.records)
        : undefined,
    [config.group_by, data],
  );

  const [drill, setDrill] = React.useState<DrilldownSelection | null>(null);

  const openDrill = React.useCallback(
    (bucket: BucketWindow, groupKeys?: (string | null)[]) =>
      setDrill({ metricName, bucket, period, groupKeys }),
    [metricName, period],
  );

  const closeDrill = React.useCallback(() => setDrill(null), []);

  const titleText = getCardTitle(metricName, config.aggregation, title);

  return (
    <div className={styles.card}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>
      {/* The caption waits for data; the grouping does not (see
       * {@link GroupByCaption}), so the row appears as soon as either has
       * something to say. */}
      {(subtext !== null || config.group_by !== undefined) && (
        <div className={styles.subtextRow}>
          {subtext !== null && (
            <span className={styles.subtext} title={subtext}>
              {subtext}
            </span>
          )}
          {config.group_by !== undefined && (
            <GroupByCaption
              groupBy={config.group_by}
              groupTruncation={groupTruncationOf(data)}
            />
          )}
        </div>
      )}

      <div className={styles.chartArea}>
        <MetricChart
          metricName={metricName}
          color={config.color}
          orgId={orgId}
          period={period}
          aggregation={config.aggregation}
          range={range}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
          display={config.display}
          groupBy={config.group_by}
          groupByType={
            filterDefinitions?.find((d) => d.field === config.group_by)?.type
          }
          axisDomain={axisDomain}
          onBucketClick={openDrill}
        />
      </div>

      <DrillDownDialog
        open={drill !== null}
        onClose={closeDrill}
        metricName={drill?.metricName ?? metricName}
        period={drill?.period ?? period}
        bucket={drill?.bucket ?? null}
        orgId={orgId}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
        condition={condition}
        filterDefinitions={filterDefinitions}
        groupBy={config.group_by}
        groupKeys={drill?.groupKeys}
        seriesKeys={seriesKeys}
        // The dialog counts every row's values in the bucket, the series
        // included, under its own filters too.
        aggregation={config.aggregation}
      />
    </div>
  );
}

/**
 * A raw card: one point per session over the window, drawn from the first
 * page immediately, with the rest available on demand beside the caption.
 *
 * Runs the SAME `rawRequestFor(...)` query as the chart it contains, so both
 * hold one React Query cache entry and one fetch — which is what lets the
 * Load all button here extend the series drawn there.
 *
 * A click opens the SAME drill-down dialog the aggregated card uses, listing
 * the one record behind the point. It lists one row rather than jumping
 * straight to the session because the table is where a record's device,
 * invocation and value live — and because a click landing on a dialog here
 * and a dialog there is one behavior to learn instead of two.
 *
 * The dialog is given a one-nanosecond window (see `instantWindow`): a raw
 * point has no width, and the dialog's half-open `>= start AND < end` filter
 * matches nothing across a zero-width range.
 */
function RawChartCardBody({
  metricName,
  title,
  orgId,
  period,
  range,
  axisDomain,
  deviceIds,
  sessionIds,
  condition,
  groupBy,
  display,
  color,
  filterDefinitions,
}: ChartCardBodyProps & { color: SeriesColor | undefined }) {
  // The raw series comes from `queryMetrics`, which takes the window in
  // nanoseconds — unlike the aggregate endpoint, which takes it in ms and
  // converts as it serializes. Converted here, at the one place the two
  // conventions meet.
  //
  // The DOMAIN's bounds, not the range's, whenever there is one. `queryMetrics`
  // filters the window literally while `aggregate_metric` snaps its own with
  // date_trunc first, so a raw card reading `range` covers a narrower span than
  // the aggregated card beside it — and switching a card off aggregation
  // visibly moved its bounds. The domain is that snapped window, so asking for
  // it makes the two cards read the same rows.
  const startTimeNs =
    axisDomain?.startNs ?? BigInt(range.start_ms) * BigInt(NS_PER_MS);
  const endTimeNs =
    axisDomain?.endNs ?? BigInt(range.end_ms) * BigInt(NS_PER_MS);

  // The point that was clicked, pinned with the label its tooltip carried, so
  // the dialog titles the exact instant it is showing rather than the calendar
  // bucket the dashboard's period would name.
  const [drill, setDrill] = React.useState<{
    bucket: BucketWindow;
    label: string;
    /** The sessions the click resolved — exactly the ones the tooltip listed.
     *  Undefined on an ungrouped card, whose one point the window already
     *  pins on its own. */
    sessionIds?: string[];
    /** The series of the clicked points, as the tooltip listed them. */
    groupKeys?: (string | null)[];
    /** Each series' sessions among the clicked points. */
    seriesCounts?: SeriesCounts;
  } | null>(null);

  const { data, hasNextPage, fetchNextPage } = useInfiniteQueryMetrics(
    rawRequestFor({
      metricName,
      startTimeNs,
      endTimeNs,
      deviceIds,
      sessionIds,
      condition,
      // Must match the chart's request field for field: an identical request is
      // what makes the two share one React Query entry and one fetch, so that
      // "Load all" here drains pages into the series the chart draws.
      groupBy,
    }),
    orgId,
  );

  // Every series loaded so far, for the drill-down to suggest first.
  const seriesKeys = React.useMemo(
    () =>
      groupBy !== undefined && data
        ? seriesKeysOf(data.pages.flatMap((p) => p.items))
        : undefined,
    [groupBy, data],
  );

  const subtext = React.useMemo(() => {
    if (!data) {
      return null;
    }
    const { points, undated } = toRawSeries(data.pages.flatMap((p) => p.items));
    return rawChartSubtext(points.length, hasNextPage, undated);
  }, [data, hasNextPage]);

  const titleText = getCardTitle(metricName, undefined, title);

  return (
    <div className={styles.card}>
      <div className={styles.title} title={titleText}>
        {titleText}
      </div>
      {/* Same rule as the aggregated card: the caption waits for data, the
       * grouping does not. "Load all" has nothing to offer until a page has
       * landed, so it keeps its own absence. */}
      {(subtext !== null || groupBy !== undefined) && (
        <div className={styles.subtextRow}>
          {subtext !== null && (
            <span className={styles.subtext} title={subtext}>
              {subtext}
            </span>
          )}
          {groupBy !== undefined && (
            <GroupByCaption
              groupBy={groupBy}
              // Every page reports the same cap for the whole query.
              groupTruncation={groupTruncationOf(data?.pages.at(-1))}
            />
          )}
          <LoadAllButton
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
          />
        </div>
      )}

      <div className={styles.chartArea}>
        <RawMetricChart
          metricName={metricName}
          color={color}
          orgId={orgId}
          startTimeNs={startTimeNs}
          endTimeNs={endTimeNs}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
          display={display}
          groupBy={groupBy}
          groupByType={
            filterDefinitions?.find((d) => d.field === groupBy)?.type
          }
          axisDomain={axisDomain}
          onPointClick={(datum, selection) => {
            // Drill into everything the click reported, not just the point
            // aimed at: that set is what the tooltip listed, so the dialog
            // lists the same rows the reader was already looking at. A lone
            // point needs no session filter — its instant pins it.
            const resolved =
              selection.length > 1 ? selectionWindow(selection) : undefined;
            setDrill({
              bucket: resolved?.bucket ?? instantWindow(datum.start_time),
              label: datum.tooltipTitle,
              sessionIds: resolved?.sessionIds,
              groupKeys: groupKeysOf(selection),
              seriesCounts: selectionSeriesCounts(selection),
            });
          }}
        />
      </div>

      <DrillDownDialog
        open={drill !== null}
        onClose={() => setDrill(null)}
        metricName={metricName}
        period={period}
        bucket={drill?.bucket ?? null}
        windowLabel={drill?.label}
        orgId={orgId}
        deviceIds={deviceIds}
        // The dashboard's session scope, and apart from it the sessions the
        // click resolved when it resolved several: those narrow the table
        // only while it shows the clicked series. They are a subset of the
        // scope — the chart they were read off was drawn under it.
        sessionIds={sessionIds}
        clickedSessionIds={drill?.sessionIds}
        // Carried like the aggregated card does: without it a filtered
        // dashboard shows a filtered chart and an unfiltered table.
        condition={condition}
        filterDefinitions={filterDefinitions}
        groupBy={groupBy}
        groupKeys={drill?.groupKeys}
        seriesKeys={seriesKeys}
        seriesCounts={drill?.seriesCounts}
      />
    </div>
  );
}
